import { useEffect, useState } from "react";

import type { ICaaOrder } from "@repo/shopify/queries/customer-account-gql";
import { OrderStatus, OrderStatusButtonType, hasPreorderCaa, orderHasTrackingCaa } from "@repo/shopify/utils/order";

/**
 * classic useOrderStatus의 CAA 대응.
 * regularOrder/preOrder 분기 구조는 classic과 동일하게 유지 — hasPreorderCaa가 아직 항상 false를
 * 반환해서 (CAA LineItem에 상품 태그가 없어 별도 구현 필요, hasPreorderCaa 주석 참고) 지금은
 * preOrder 분기가 실질적으로 타지 않지만, 나중에 태그 조회가 붙으면 바로 동작하도록 구조를 미리 맞춰둠.
 * CAA 스키마엔 customAttributes(isGifting)가 없어 기프팅 세부 분기는 판별 불가해 생략.
 * jp는 반품(RETURN)뿐 아니라 주문 취소(CANCEL)도 제공하지 않아 두 버튼 모두 절대 노출하지 않음
 * — 배송 시작 전이라 classic이라면 취소 가능한 상태도 activeButton은 NONE으로 두고 orderStatus 텍스트만 그대로 노출
 */
const useOrderStatusCaa = (order?: ICaaOrder | null) => {
  const [activeButton, setActiveButton] = useState<OrderStatusButtonType>(OrderStatusButtonType.NONE);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>(OrderStatus.None);

  const handleOrderStatus = (type: OrderStatusButtonType, status: OrderStatus) => {
    setActiveButton(type);
    setOrderStatus(status);
  };

  useEffect(() => {
    if (!order) return;

    const returnStatus = order.returnStatus?.value;
    const isOffline = order.isOffline?.value;

    const orderStatusHandlers: Record<"preOrder" | "regularOrder", Record<string, () => void>> = {
      regularOrder: {
        PAID: () => {
          // 고객이 주문 완료 후 '일부 상품'을 hold 했을때 — classic이면 아직 발송 전이라 취소 가능하지만 jp는 취소 미제공
          if (order.fulfillmentStatus === "ON_HOLD") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed);
            return;
          }

          // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
          if (order.fulfillmentStatus === "UNFULFILLED") {
            // 이미 취소된 (0원 이벤트 쿠폰 등) 주문일 때
            if (order.cancelReason) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
              return;
            }

            // classic이면 아직 발송 전이라 취소 가능하지만 jp는 취소 미제공
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed);
            return;
          }

          if (order.fulfillmentStatus === "FULFILLED") {
            // 고객이 매장에서 주문 시
            if (isOffline) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed);
              return;
            }

            // 반품 신청을 했을 때 (CS팀 등을 통해 접수된 경우) — jp는 반품 버튼은 없지만 상태 텍스트는 노출
            if (returnStatus === "requested") {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Requested);
              return;
            }

            // 반품 신청이 승인 되었을 때
            if (returnStatus === "approved") {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Approved);
              return;
            }

            // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때 — 이미 발송되어 취소 불가
            if (orderHasTrackingCaa(order)) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Shipped);
              return;
            }

            // 고객이 주문 완료 후 비젬에서 주문 확정했을 때 — classic이면 아직 발송 전이라 취소 가능하지만 jp는 취소 미제공
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Preparing);
          }
        },
        PARTIALLY_PAID: () => {
          if (order.fulfillmentStatus !== "FULFILLED") return;

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 신청을 했을 때
          if (returnStatus === "requested") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Requested);
            return;
          }

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 승인이 되었을 때
          if (returnStatus === "approved") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Approved);
            return;
          }

          // 반품 처리중인 주문이 존재하지 않을 때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Refunded);
        },
        PARTIALLY_REFUNDED: () => {
          if (order.fulfillmentStatus !== "FULFILLED") return;

          if (returnStatus === "requested") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Requested);
            return;
          }

          if (returnStatus === "approved") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Approved);
            return;
          }

          // 모든 주문이 반품되었지만 반품배송비때문에 부분환불로 보이는 경우 포함
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Partially);
        },
        REFUNDED: () => {
          // 고객 또는 CS팀에서 주문을 취소했을 때
          if (order.cancelReason) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
            return;
          }

          // 반품 신청 후 CS팀에서 승인한 뒤 환불 처리까지 완료했을 때 / 매장 구매 후 환불한 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Refunded);
        },
        VOIDED: () => {
          // 결제 승인만 된 상태에서, 실제로 금액이 청구되기 전에 취소되었을때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
        },
        PENDING: () => {
          // 아직 결제가 진행중인 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Pending);
        },
      },
      preOrder: {
        PAID: () => {
          // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
          if (order.fulfillmentStatus === "UNFULFILLED") {
            if (order.cancelReason) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
              return;
            }

            // classic이면 아직 발송 전이라 취소 가능하지만 jp는 취소 미제공
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed);
            return;
          }

          if (order.fulfillmentStatus === "FULFILLED") {
            // 반품 신청을 했을 때
            if (returnStatus === "requested") {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Requested);
              return;
            }

            // 반품 신청이 승인 되었을 때
            if (returnStatus === "approved") {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Return_Approved);
              return;
            }

            // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
            if (orderHasTrackingCaa(order)) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed_Preparing);
              return;
            }

            // 1. 고객이 주문 완료 후 비젬에서 주문 확정했을 때
            // 2. 드로우 주문: 당첨된 오프라인 드로우 주문이면서 비젬에서 주문 확정했을 때
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Scheduled_Preparing);
            return;
          }

          // OnHold 상태인 경우 — classic이면 아직 발송 전이라 취소 가능하지만 jp는 취소 미제공
          if (order.fulfillmentStatus === "ON_HOLD") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Scheduled);
          }
        },
        REFUNDED: () => {
          // 고객 또는 CS팀에서 주문을 취소했을 때
          if (order.cancelReason) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
            return;
          }

          // 모든 제품에 대해 고객이 반품 신청 후, CS팀에서 승인한 뒤 환불 처리까지 완료했을 때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Refunded);
        },
        VOIDED: () => {
          // 결제 승인만 된 상태에서, 실제로 금액이 청구되기 전에 취소되었을때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
        },
        PENDING: () => {
          // 아직 결제가 진행중인 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Pending);
        },
      },
    };

    if (order.financialStatus) {
      const orderType = hasPreorderCaa(order) ? "preOrder" : "regularOrder";
      orderStatusHandlers[orderType][order.financialStatus]();
    }
  }, [order]);

  return { activeButton, orderStatus };
};

export default useOrderStatusCaa;
